import type { AvailabilityIssue, DialogEffect, DialogEvent, DialogState } from '~/orderingApp/types';

export const initialState: DialogState = {
  context: {
    autoOpen: true,
    cartFormHasSubmitted: false,
    priceChangeAcknowledged: false,
  },
  effects: [],
  previousValue: 'none',
  value: 'none',
};

const addEffect = (effect: DialogEffect, effects: DialogEffect[]) => {
  if (effects.includes(effect)) {
    return [...effects];
  } else {
    return [...effects, effect];
  }
};

const removeEffect = (effect: DialogEffect, effects: DialogEffect[]) => {
  if (effects.includes(effect)) {
    return effects.filter(effect_ => effect_ !== effect);
  } else {
    return [...effects];
  }
};

const cleanContext = (context: DialogState['context']) => ({
  ...initialState.context,
  autoOpen: context.autoOpen,
  cartFormHasSubmitted: context.cartFormHasSubmitted,
});

const duplicateState = (state: DialogState) => ({ ...state, context: { ...state.context }, previousValue: state.value });

const withAvailabilityIssue = (state: DialogState, issue: AvailabilityIssue): DialogState => {
  if (issue === 'noMenus' || issue === 'closedLocation') return { ...state, value: issue };

  return { ...state, context: { ...state.context, issue }, value: 'availabilityAlert' };
};

export const dialogReducer = (prevState: DialogState = initialState, event: DialogEvent) => {
  const nextState = duplicateState(prevState);

  /**
   * Non state-dependent events (Global)
   * cases should return, skipping state specific handling and blocking state transitions
   * default should break to fall through to state specific handling
   */
  switch (event.type) {
    case 'ordering.resetState':
      return initialState;
    case 'ordering.dialogEffect':
      if (event.completeEffect) {
        nextState.effects = removeEffect(event.completeEffect, nextState.effects);
      }
      return nextState;
    case 'ordering.disableAutoOpenDialog':
      nextState.context.autoOpen = false;
      return nextState;
    case 'ordering.enableAutoOpenDialog':
      nextState.context.autoOpen = true;
      nextState.context.cartFormHasSubmitted = false;
      return nextState;
    case 'ordering.resetPriceChangeAcknowledgement':
      nextState.context.priceChangeAcknowledged = false;
      return nextState;
    default:
      break;
  }

  // State dependent events
  switch (prevState.value) {
    case 'none':
      switch (event.type) {
        case 'ordering.navigateToSignIn':
          nextState.value = 'signIn';
          break;
        case 'ordering.navigateToAddPhone':
          nextState.value = 'signIn';
          nextState.context.signInDialogOpenTo = 'requirePhone';
          break;
        case 'ordering.navigateToValidatePhone':
          nextState.value = 'signIn';
          nextState.context.signInDialogOpenTo = 'phoneAuthCode';
          nextState.context.signInValidatePhoneContext = event.context;
          break;
        case 'ordering.availabilityChanged':
          Object.assign(nextState, withAvailabilityIssue(nextState, event.context.issue));
          break;
        case 'ordering.alert':
          switch (event.context.issue) {
            case 'invalidItems':
            case 'invalidScheduledItems':
              nextState.value = 'invalidCartItems';
              nextState.context.issue = event.context.issue;
              break;
            case 'invalidOrderInfo':
              nextState.value = 'availabilityAlert';
              nextState.context.issue = 'invalidOrderInfo';
              break;
            case 'orderingBlackout':
              nextState.value = 'availabilityAlert';
              nextState.context.issue = 'orderingBlackout';
              break;
            case 'noMenus':
              nextState.value = 'noMenus';
              break;
            case 'priceChangeAlert':
              nextState.context.priceChangeAcknowledged = true;
              nextState.value = 'priceChangeAlert';
              break;
            case 'invalidSubmit':
              nextState.value = 'invalidSubmit';
              nextState.context.errorMessage = event.context.errorMessage;
              nextState.context.errorTitle = event.context.errorTitle;
              nextState.context.errorName = event.context.errorName;
              break;
            case 'closedLocation':
              nextState.value = 'closedLocation';
              break;
            case 'cartItemsPersistNone':
              nextState.value = 'cartItemsPersistNone';
              nextState.context.issue = event.context.issue;
              break;
            case 'cartItemsPersistPartial':
              nextState.value = 'cartItemsPersistPartial';
              nextState.context.issue = event.context.issue;
              break;
            default:
              break;
          }
          break;
        case 'ordering.autoOpenDialog': {
          const { cart, dishIdParam, isHomePage, isOrderingEvent, isStandard, isDeliveryOnly, isPickupDefault, isDeliveryDefault, isCatering, pickupWindowType, locationParam, restaurant } = event.context;
          if (!nextState.context.autoOpen) break;
          if (isHomePage && locationParam) break; // locationParam handling in autoNavigateEffect should occur before autoOpenDialog transitions state
          switch (true) {
            case isOrderingEvent && pickupWindowType === 'arrive_same_time':
              nextState.value = 'availabilityAlert';
              nextState.context.autoOpen = false;
              break;
            case isOrderingEvent && pickupWindowType === 'arrive_multi_time_window':
              nextState.value = 'cartScheduler';
              nextState.context.autoOpen = false;
              break;
            case isOrderingEvent && pickupWindowType === 'arrive_time_window':
              nextState.value = 'cartScheduler';
              nextState.context.autoOpen = false;
              break;
            case isStandard && restaurant.hasMultipleLocations && isHomePage:
              nextState.value = 'locationSelection';
              nextState.context.autoOpen = false;
              break;
            case cart?.fulfillmentType === 'delivery_fulfillment_type' && !cart.deliveryAddress?.full:
              nextState.value = 'locationSelection';
              nextState.context.autoOpen = false;
              break;
            case isStandard && restaurant.hasOnlyOneOrderableLocation && (!restaurant.hasMultipleLocations || dishIdParam) && isPickupDefault && !isDeliveryOnly:
              nextState.value = 'none';
              nextState.context.autoOpen = false;
              break;
            case isStandard && restaurant.hasOnlyOneOrderableLocation && (isDeliveryDefault || isDeliveryOnly):
              nextState.value = 'locationSelection';
              nextState.context.autoOpen = false;
              break;
            case isStandard && restaurant.hasMultipleOrderableLocations && isPickupDefault && !isDeliveryOnly && isHomePage:
              nextState.value = 'locationSelection';
              nextState.context.autoOpen = false;
              break;
            case isStandard && restaurant.hasMultipleOrderableLocations && (isDeliveryDefault || isDeliveryOnly) && cart.id === -1:
              nextState.value = 'locationSelection';
              nextState.context.autoOpen = false;
              break;
            case isCatering && restaurant.hasOnlyOneOrderableLocation && restaurant.hasMultipleLocations && !dishIdParam && isPickupDefault && !isDeliveryOnly:
              nextState.value = 'locationSelection';
              nextState.context.autoOpen = false;
              break;
            case isCatering && restaurant.hasOnlyOneOrderableLocation && (!restaurant.hasMultipleLocations || dishIdParam) && isPickupDefault && !isDeliveryOnly:
              nextState.value = 'cartScheduler';
              nextState.context.autoOpen = false;
              break;
            case isCatering && restaurant.hasOnlyOneOrderableLocation && (isDeliveryDefault || isDeliveryOnly):
              nextState.value = 'locationSelection';
              nextState.context.autoOpen = false;
              break;
            case isCatering && restaurant.hasMultipleOrderableLocations && isPickupDefault && !isDeliveryOnly:
              nextState.value = 'locationSelection';
              nextState.context.autoOpen = false;
              break;
            case isCatering && restaurant.hasMultipleOrderableLocations && (isDeliveryDefault || isDeliveryOnly):
              nextState.value = 'locationSelection';
              nextState.context.autoOpen = false;
              break;
            default:
              break;
          }
          break;
        }
        case 'ordering.seeAuthorizeNetPaymentIFrame':
          nextState.value = 'authorize_net_payment';
          if (event.context) {
            nextState.context = { ...nextState.context, ...event.context };
          }
          break;
        case 'ordering.seeProfile':
          if (!event.context.isSignedIn) {
            nextState.value = 'signIn';
          }
          break;
        case 'ordering.seePrivacyPolicy':
          nextState.value = 'privacyPolicy';
          break;
        case 'ordering.seeOrderInfo':
          if (event.context?.fulfillmentType === 'delivery_fulfillment_type') {
            nextState.value = 'locationSelection';
            nextState.effects = addEffect('changeToDelivery', nextState.effects);
          } else if (event.context?.fulfillmentType === 'pickup_fulfillment_type') {
            nextState.value = 'locationSelection';
            nextState.effects = addEffect('changeToPickup', nextState.effects);
          } else {
            nextState.value = 'orderInfo';
          }
          break;
        case 'ordering.seeItem':
          nextState.value = 'menuItemDetail';
          nextState.context.cartItemId = event.context?.cartItemId;
          nextState.context.menuItemId = event.context?.menuItemId;
          nextState.context.reorderId = event.context?.reorderId;
          nextState.context.source = event.context?.source;
          break;
        case 'ordering.seeMyCart':
          nextState.value = 'shoppingCart';
          break;
        case 'ordering.seeHours':
          nextState.value = 'menuHours';
          break;
        case 'ordering.scheduleForLater':
          nextState.value = 'cartScheduler';
          break;
        case 'ordering.seeCodeInfo':
          nextState.value = 'seeCodeInfo';
          nextState.context.codeType = event.context.codeType;
          nextState.context.offerId = event.context.offerId;
          break;
        case 'ordering.showNcrPayment':
          nextState.value = 'ncrPayment';
          break;
        case 'ordering.seeConsumerProfileOfferDetails':
          nextState.value = 'consumerProfileOfferDetails';
          nextState.context.offerData = event.context.offerData;
          break;
        default:
          break;
      }
      break;
    case 'consumerProfileOfferDetails':
      switch (event.type) {
        case 'ordering.dismissDialog':
          nextState.value = 'none';
          break;
        default:
          break;
      }
      break;
    case 'orderInfo':
      switch (event.type) {
        case 'ordering.changeLocation':
          if (event.context?.fulfillmentType === 'delivery_fulfillment_type') {
            nextState.effects = addEffect('changeToDelivery', nextState.effects);
          }
          nextState.value = 'locationSelection';
          break;
        case 'ordering.scheduleForLater':
          nextState.value = 'cartScheduler';
          break;
        case 'ordering.updateCartInfoSuccess':
          nextState.context.cartFormHasSubmitted = !!event.context?.cartFormHasSubmitted;
          nextState.value = 'none';
          break;
        case 'ordering.dismissDialog':
          nextState.effects = addEffect('resetCartInfoForm', nextState.effects);
          nextState.value = 'none';
          break;
        case 'ordering.seeItem':
          nextState.context.cartFormHasSubmitted = !!event.context?.cartFormHasSubmitted;
          nextState.value = 'menuItemDetail';
          break;
        default:
          break;
      }
      break;
    case 'cartScheduler':
      switch (event.type) {
        case 'ordering.dismissDialog':
          nextState.value = 'orderInfo';
          break;
        case 'ordering.selectTime':
          nextState.value = 'orderInfo';
          break;
        case 'ordering.seeItem':
          nextState.context.cartFormHasSubmitted = !!event.context?.cartFormHasSubmitted;
          nextState.value = 'menuItemDetail';
          break;
        default:
          break;
      }
      break;
    case 'menuHours':
      switch (event.type) {
        case 'ordering.dismissDialog':
          nextState.value = 'none';
          break;
        default:
          break;
      }
      break;
    case 'locationSelection':
      switch (event.type) {
        case 'ordering.changeLocation':
          nextState.value = 'orderInfo';
          nextState.context.locationName = event.context?.locationName;
          break;
        case 'ordering.updateCartInfoSuccess':
          nextState.context.cartFormHasSubmitted = !!event.context?.cartFormHasSubmitted;
          nextState.value = 'none';
          break;
        case 'ordering.scheduleForLater':
          nextState.value = 'cartScheduler';
          break;
        case 'ordering.seeItem':
          nextState.context.cartFormHasSubmitted = !!event.context?.cartFormHasSubmitted;
          nextState.value = 'menuItemDetail';
          break;
        case 'ordering.dismissDialog':
          nextState.effects = addEffect('resetCartInfoForm', nextState.effects);
          nextState.value = 'none';
          break;
        default:
          break;
      }
      break;
    case 'menuItemDetail':
      switch (event.type) {
        case 'ordering.dismissDialog':
          if (prevState.context.source === 'cartUpsell') {
            nextState.value = 'shoppingCart';
            nextState.context = cleanContext(nextState.context);
            break;
          }
          nextState.value = prevState.previousValue === 'signIn' || prevState.previousValue === 'menuItemModifier' || prevState.previousValue === 'menuItemDetail' || prevState.previousValue === 'locationSelection' || prevState.previousValue === 'orderInfo' ? 'none' : prevState.previousValue;
          nextState.context = cleanContext(nextState.context);
          break;
        case 'ordering.seeItem':
          nextState.context.cartItemId = event.context?.cartItemId;
          nextState.context.reorderId = event.context?.reorderId;
          nextState.context.source = event.context?.source;
          nextState.context.menuItemId = event.context?.menuItemId;
          break;
        case 'ordering.addCartItemSuccess':
          if (prevState.context.source === 'cartUpsell') {
            nextState.value = 'shoppingCart';
            nextState.context = cleanContext(nextState.context);
            break;
          }
          switch (event.context.viewportCurrentSize) {
            case 'desktop':
              nextState.value = 'shoppingCart';
              nextState.context = cleanContext(nextState.context);
              break;
            case 'mobile':
              nextState.value = 'none';
              nextState.context = cleanContext(nextState.context);
              break;
            default:
              break;
          }
          break;
        case 'ordering.updateCartItemSuccess':
          nextState.value = 'shoppingCart';
          nextState.context = cleanContext(nextState.context);
          break;
        case 'ordering.seeProfile':
          nextState.context.menuItemId = prevState.context.menuItemId;
          nextState.context.popTypeTrigger = event.context.popTypeTrigger;
          nextState.value = 'signIn';
          break;
        case 'ordering.openMenuItemModifier':
          nextState.value = 'menuItemModifier';
          nextState.context = { ...prevState.context, menuItemModifierIds: [event.context.menuItemModifierId] };
          break;
        default:
          break;
      }
      break;
    case 'menuItemModifier':
      switch (event.type) {
        case 'ordering.dismissDialog':
          nextState.value = prevState.context.menuItemModifierIds && prevState.context.menuItemModifierIds.slice(0, -1).length === 0 ? 'menuItemDetail' : prevState.value;
          nextState.context = { ...prevState.context, menuItemModifierIds: prevState.context.menuItemModifierIds && prevState.context.menuItemModifierIds.slice(0, -1), parentModifierGroupId: event.context?.parentModifierGroupId };
          break;
        case 'ordering.openMenuItemModifier':
          nextState.context = { ...prevState.context, menuItemModifierIds: [...prevState.context.menuItemModifierIds || [], event.context.menuItemModifierId] };
          break;
        default:
          break;
      }
      break;
    case 'invalidSubmit':
      switch (event.type) {
        case 'ordering.dismissDialog':
          nextState.value = 'none';
          nextState.context = cleanContext(nextState.context);
          break;
        default:
          break;
      }
      break;
    case 'privacyPolicy':
      switch (event.type) {
        case 'ordering.dismissDialog':
          nextState.value = 'none';
          break;
        default:
          break;
      }
      break;
    case 'shoppingCart':
      switch (event.type) {
        case 'ordering.seeItem':
          nextState.value = 'menuItemDetail';
          if (event.context) {
            nextState.context = { ...nextState.context, ...event.context };
          }
          break;
        case 'ordering.keepShopping':
          nextState.value = 'none';
          break;
        case 'ordering.dismissDialog':
          nextState.value = 'none';
          break;
        case 'ordering.navigateToAddPhone':
          nextState.value = 'signIn';
          nextState.context.signInDialogOpenTo = 'requirePhone';
          break;
        case 'ordering.navigateToSignIn':
          nextState.value = 'signIn';
          break;
        case 'ordering.navigateToValidatePhone':
          nextState.value = 'signIn';
          nextState.context.signInDialogOpenTo = 'phoneAuthCode';
          nextState.context.signInValidatePhoneContext = event.context;
          break;
        case 'ordering.seeProfile':
          switch (event.context.isSignedIn) {
            case false:
              nextState.value = 'signIn';
              break;
            default:
              break;
          }
          break;
        case 'ordering.seeCodeInfo':
          nextState.value = 'seeCodeInfo';
          nextState.context.codeType = event.context.codeType;
          nextState.context.offerId = event.context.offerId;
          break;
        case 'ordering.goToCheckout':
          nextState.value = 'none';
          break;
        case 'ordering.alert':
          switch (event.context.issue) {
            case 'invalidItems':
              nextState.value = 'invalidCartItems';
              nextState.context.issue = event.context.issue;
              break;
            case 'invalidScheduledItems':
              nextState.value = 'invalidCartItems';
              nextState.context.issue = event.context.issue;
              break;
            case 'priceChangeAlert':
              nextState.context.priceChangeAcknowledged = true;
              nextState.value = 'priceChangeAlert';
              break;
            case 'invalidSubtotal':
              nextState.value = 'invalidSubtotal';
              break;
            default:
              break;
          }
          break;
        default:
          break;
      }
      break;
    case 'invalidCartItems':
      switch (event.type) {
        case 'ordering.dismissDialog':
          nextState.value = 'none';
          nextState.context = cleanContext(nextState.context);
          break;
        default:
          break;
      }
      break;
    case 'signIn':
      switch (event.type) {
        case 'ordering.signInSuccess':
          nextState.value = 'none';
          break;
        case 'ordering.dismissDialog':
          nextState.value = 'none';
          break;
        case 'ordering.seeItem':
          nextState.value = 'menuItemDetail';
          if (event.context) {
            nextState.context = { ...nextState.context, ...event.context };
          }
          break;
        default:
          break;
      }
      break;
    case 'availabilityAlert':
      switch (event.type) {
        case 'ordering.alert':
          nextState.context.issue = event.context.issue;
          break;
        case 'ordering.availabilityChanged':
          Object.assign(nextState, withAvailabilityIssue(nextState, event.context.issue));
          break;
        case 'ordering.dismissDialog':
          nextState.value = 'none';
          nextState.context.issue = undefined;
          break;
        case 'ordering.scheduleForLater':
          nextState.value = 'cartScheduler';
          break;
        case 'ordering.seeOrderInfo':
          nextState.value = 'orderInfo';
          break;
        case 'ordering.seeItem':
          nextState.value = 'menuItemDetail';
          break;
        default:
          break;
      }
      break;
    case 'priceChangeAlert':
      switch (event.type) {
        case 'ordering.dismissDialog':
          nextState.value = 'none';
          break;
        default:
          break;
      }
      break;
    case 'invalidSubtotal':
      switch (event.type) {
        case 'ordering.dismissDialog':
          nextState.value = 'shoppingCart';
          break;
        default:
          break;
      }
      break;
    case 'noMenus':
      switch (event.type) {
        case 'ordering.availabilityChanged':
          Object.assign(nextState, withAvailabilityIssue(nextState, event.context.issue));
          break;
        case 'ordering.dismissDialog':
          nextState.value = 'none';
          break;
        default:
          break;
      }
      break;
    case 'seeCodeInfo':
      switch (event.type) {
        case 'ordering.dismissDialog':
          if (prevState.previousValue === 'seeCodeInfo') {
            nextState.value = 'none';
          } else {
            nextState.value = prevState.previousValue;
          }
          break;
        case 'ordering.seeProfile':
          nextState.value = 'signIn';
          break;
        default:
          break;
      }
      break;
    case 'authorize_net_payment':
      switch (event.type) {
        case 'ordering.dismissDialog':
          nextState.value = 'none';
          break;
        default:
          break;
      }
      break;
    case 'closedLocation':
      switch (event.type) {
        case 'ordering.availabilityChanged':
          Object.assign(nextState, withAvailabilityIssue(nextState, event.context.issue));
          break;
        case 'ordering.dismissDialog':
          nextState.value = 'none';
          break;
        default:
          break;
      }
      break;
    case 'ncrPayment':
      switch (event.type) {
        case 'ordering.dismissDialog':
          nextState.value = 'none';
          break;
        default:
          break;
      }
      break;
    case 'cartItemsPersistNone':
    case 'cartItemsPersistPartial':
      switch (event.type) {
        case 'ordering.dismissDialog':
          nextState.value = 'none';
          break;
        default:
          break;
      }
      break;
  }

  if (nextState.value === 'none') {
    nextState.context.signInDialogOpenTo = undefined;
    nextState.context.signInValidatePhoneContext = undefined;
  }
  return nextState;
};
